










































import { Component, Vue } from 'vue-property-decorator';
import { Config } from '@/config';
import { SquareFieldState } from '@/types';
import SquareMaterialField from '@/components/common/SquareMaterialField.vue';

export interface SquarePaymentFormCI extends Vue {
  requestCardNonce: () => void;
}

const initialSquareFieldState = {
  pristine: true,
  hasErrors: false,
  hasErrorClass: false,
  hasFocusClass: false,
  isCompletelyValid: false,
  isEmpty: true,
  isPotentiallyValid: false,
};

@Component({
  components: { SquareMaterialField },
})
export default class SquarePaymentForm extends Vue implements SquarePaymentFormCI {
  private paymentForm!: SqPaymentForm;

  private formState: { [key: string]: SquareFieldState } = {
    cardNumber: { ...initialSquareFieldState },
    expirationDate: { ...initialSquareFieldState },
    cvv: { ...initialSquareFieldState },
    postalCode: { ...initialSquareFieldState },
  };

  private isFormValid = false;

  private responseErrors: string[] = [];

  private responseErrorsTimeout: number | null = null;

  mounted() {
    this.init();
  }

  beforeDestroy() {
    this.paymentForm.destroy();
  }

  private checkIsFormValid(): void {
    const isValid = !Object.values(this.formState).some((e) => !e.isCompletelyValid);
    if (this.isFormValid !== isValid) {
      this.isFormValid = isValid;
      this.$emit('changeFormValid', isValid);
    }
  }

  private init(): void {
    // Create and initialize a payment form object
    this.paymentForm = new window.SqPaymentForm({
      // Initialize the payment form elements
      applicationId: Config.SquareConfig.squareApplicationId,
      inputClass: 'sq-input',
      autoBuild: false,
      // Customize the CSS for SqPaymentForm iframe elements
      inputStyles: [{
        fontSize: '16px',
        lineHeight: '28px',
        placeholderColor: '#a0a0a0',
        backgroundColor: 'transparent',
      }],
      // Initialize the credit card placeholders
      cardNumber: {
        elementId: 'sq-card-number',
      },
      cvv: {
        elementId: 'sq-cvv',
      },
      expirationDate: {
        elementId: 'sq-expiration-date',
        placeholder: 'MM/YY',
      },
      postalCode: {
        elementId: 'sq-postal-code',
      },
      // SqPaymentForm callback functions
      callbacks: {
        paymentFormLoaded: () => {
          this.$emit('payment-form-loaded');
        },
        /*
        * callback function: cardNonceResponseReceived
        * Triggered when: SqPaymentForm completes a card nonce request
        */
        cardNonceResponseReceived: (
          errors: Array<{ field: string; message: string }> | undefined, nonce: string,
        ): void => {
          if (this.responseErrorsTimeout) {
            this.responseErrors = [];
            clearTimeout(this.responseErrorsTimeout);
          }

          if (errors) {
            const fieldErrors = errors.filter((e) => !!e.field);
            const otherErrors = errors.filter((e) => !e.field);

            this.responseErrors = otherErrors.map((e) => e.message);
            this.responseErrorsTimeout = setTimeout(() => { this.responseErrors = []; }, 5000);

            fieldErrors.forEach((error) => {
              this.$set(this.formState[error.field], 'errorMessage', error.message);
            });
            this.$emit('card-nonce-response-error', nonce);
            return;
          }

          this.$emit('card-nonce-response-received', nonce);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inputEventReceived: (inputEvent: any): void => {
          const pristine = this.formState[inputEvent.field].pristine && inputEvent.eventType === 'focusClassAdded';

          let hasErrors = inputEvent.currentState.hasErrorClass;

          if (inputEvent.eventType === 'focusClassRemoved' && !hasErrors) {
            hasErrors = inputEvent.currentState.isEmpty || !inputEvent.currentState.isCompletelyValid;
            // Focus to expirationDate field
            if (inputEvent.field === 'cardNumber' && !hasErrors) {
              this.paymentForm.focus('expirationDate');
            }
          }

          this.formState[inputEvent.field] = {
            pristine,
            hasErrors,
            ...inputEvent.currentState,
          };

          this.checkIsFormValid();
        },
      },
    });

    this.paymentForm.build();
  }

  public requestCardNonce(): void {
    this.paymentForm.requestCardNonce();
  }
}
